import { Button } from '@jouzen/ecom-components/v3/Button';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useLocale, useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import RichText from '@/app/components/RichText';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';

import {
  PRODUCT_RECOMMENDATIONS_CONFIG,
  type RecommendedProductKey,
} from './_data/productRecommendationsConfig';
import { ProductRecommendationCard } from './ProductRecommendationCard';

/**
 * Props for the MaxQuantityReachedMessage component
 * @property description - Translation key for the message explaining the quantity limit
 * @property fullWidth - Whether the component should take full width (removes max-width constraint on mobile)
 * @property limit - The maximum quantity allowed (used in the description message)
 * @property recommendedProducts - Array of product keys to display as recommendations (defaults to or4Charger and dexcomStelo)
 */
export interface MaxQuantityReachedMessageProps {
  readonly description: MessageKey;
  readonly fullWidth?: boolean;
  readonly limit: number;
  readonly recommendedProducts?: RecommendedProductKey[];
}

const MaxQuantityReachedMessage = ({
  description,
  fullWidth,
  limit,
  recommendedProducts = ['or4Charger', 'dexcomStelo'],
}: MaxQuantityReachedMessageProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const localePartialUrl = getLocalePartialUrl(locale);

  const cartUrl = `${localePartialUrl}cart`;

  return (
    <section
      className={cx('mt-5 w-full md:mt-3', {
        'mx-auto max-lg:max-w-sm': !fullWidth,
      })}
      data-cy="max-quantity-reached-message"
    >
      <Typography className="mb-6 text-base font-normal">
        <RichText messageKey={description} tValues={{ amount: limit }} />
      </Typography>
      <Button
        className="w-full border-gray-400 hover:border-gray-500"
        href={cartUrl}
        variant="outlined-dark"
        data-cy="view-cart-button"
      >
        <RichText messageKey="view_your_cart" />
      </Button>
      <Typography className="my-6 text-xl font-normal" Element="h2">
        {t('ring_pdp_2025_explore_other_products')}
      </Typography>
      <div className="flex flex-col gap-y-6">
        {recommendedProducts.map((key) => {
          const config = PRODUCT_RECOMMENDATIONS_CONFIG[key];
          return <ProductRecommendationCard key={key} {...config} />;
        })}
      </div>
    </section>
  );
};

export default MaxQuantityReachedMessage;
